import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useFeatureEnabled } from 'Components/FeatureToggle';
import { HasPermission } from 'Core/security';
import {
	OEM_DATA_PERMISSION,
	OEM_DATA_USER_CODE,
	OEM_RAW_DATA_CBX_ONLY_DEFAULT,
	OEM_RAW_DATA_CBX_ONLY_FLAG,
} from '../constants';
import { canViewOemData } from '../utils/rawDataAccess';
import { hasEventId } from './useDeviceReadingRawData';
import { OemDataModal } from '../OemDataModal';

/**
 * Everything an entry point needs to offer the OEM data action, whatever it
 * renders as its trigger (OemDataButton, or the InfoBubble header's
 * TabIconButton, which `Components/Tabs` only places in its toolbar by type):
 * - `visible`: false without the `oemdata` permission (account role, plus userCode 379
 *   for a sub-user) or for a unit the `oem-raw-data-cbx-only` setting excludes;
 * - `disabled`: no unit or no real event yet (initial readings start at `rowId: -1`);
 * - `tooltip` / `label`: translated texts;
 * - `open()`: snapshots `{ unitId, eventId, unitLabel }` so live updates cannot
 *   swap the event under the reader;
 * - `modal`: the modal, portal'd to the body (out of the map overlay), or null
 *   while closed — render it anywhere in the host.
 */
export const useOemDataAction = ({ unitId, eventId, manufacturerName, unitLabel }) => {
	const { messages, formatMessage } = useIntl();
	const cbxOnly = useFeatureEnabled(OEM_RAW_DATA_CBX_ONLY_FLAG, OEM_RAW_DATA_CBX_ONLY_DEFAULT);
	const user = useSelector((state) => state.securityRedux?.user);
	const [snapshot, setSnapshot] = useState(null);

	const visible =
		HasPermission(user, [OEM_DATA_PERMISSION], [OEM_DATA_USER_CODE]) && canViewOemData(manufacturerName, cbxOnly);
	const disabled = !unitId || !hasEventId(eventId);
	let tooltip = formatMessage({ id: 'oemDataModal_openFor' }, { name: unitLabel || '' });
	if (disabled) tooltip = messages['oemDataModal_noEvent'];

	const open = () => {
		if (disabled) return;
		setSnapshot({ unitId, eventId: Number(eventId), unitLabel });
	};

	let modal = null;
	if (snapshot) {
		modal = createPortal(
			<OemDataModal
				unitId={snapshot.unitId}
				eventId={snapshot.eventId}
				unitLabel={snapshot.unitLabel}
				showRawJson={!cbxOnly}
				onClose={() => setSnapshot(null)}
			/>,
			document.body,
		);
	}

	return { visible, disabled, tooltip, label: messages['oemDataModal_open'], open, modal };
};
